import * as React from "react";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { IncomePaymentDetails } from "./IncomePaymentDetails/IncomePaymentDetails";
import { FinancialAdvice } from "./FinancialAdvice";
import { Declaration } from "./Declaration";
import { PensionContributionModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContributionModel";
import { InstructionDocumentTypeRequestTriggerActionsCreator } from "../../../Common/Documents/Actions/InstructionDocumentTypeRequestTriggerActionsCreator";
import { DocumentCategory } from "../../../../../../../Wrap/Constants/Enums/Domain/DocumentCategory";
import { IllustrationsAvailable } from "../../../Common/Documents/Components/IllustrationsAvailable";
import { ChooseAssetsToSellAssets } from "../../../Common/SIPP/DrawdownDetails/Store/DrawdownCalculationStoreStatic";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { SubAccountTypeHelper } from "../../../Common/Helpers/SubAccountTypeHelper";
import {
    getSellableAssets,
    getUpdatedAssetsToSell,
} from "../../../Common/SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import { incomePaymentDetailsLabels } from "../../../Common/SIPP/IncomePaymentDetails/Resource/Content";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { IncomePaymentDestination } from "../../../../../../../Wrap/Constants/Enums/Domain/IncomePaymentDestination";

interface IReviewProps extends IProcessComponentProps {
    type: SubAccountType;
    data: PensionContributionModel;
    arrangementId?: number;
}

interface IReviewState {
    hasChecked: any[];
    assets: ChooseAssetsToSellAssets[];
}


const bankAccountDetailsConfig = (incomePaymentAllocations, contribution) => {
    if (!incomePaymentAllocations || (incomePaymentAllocations ?? []).every(x => x.Destination == IncomePaymentDestination.NotSpecified)) {
        return [];
    }

    const bankAccountOptions = generateBankAccountOptions(
        contribution.AllBankAccounts ?? [],
        contribution.BankAccountInstructions ?? []
    );

    const bankAccountDetails = [];

    incomePaymentAllocations.forEach((bankAccount, index) => {
        const accountId = Number(bankAccount.BankAccountId); 
        const data = bankAccountOptions.get(accountId);

        bankAccountDetails.push(
            {
                title: `Bank account ${index + 1}`,
                data, 
            },
            {
                title: "Portion of income",
                data:
                    bankAccount?.Allocation === 0 && incomePaymentAllocations.length === 1
                        ? 100
                        : bankAccount?.Allocation,
            }
        );
    });

    return bankAccountDetails;
};


const generateBankAccountOptions = (
    allBankAccounts: any[] = [],
    bankAccountInstructions: any[] = []
): Map<number, string> => {
    const options = new Map<number, string>();
    options.set(-1, incomePaymentDetailsLabels.wrapCash); // wrapCash
    if (!isNullOrEmpty(allBankAccounts) ) {
            allBankAccounts.forEach((account) => {
                if (account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint) {
                    options.set(
                        account.BankAccountId,
                        `${account?.SortCode || ""} | ${account?.AccountNumber || ""} | ${account.HolderName || ""}`
                    );
                }
            });
        }

        //by requirement - new added bank account is allowed in Edit income wizard.
        if (!isNullOrEmpty(bankAccountInstructions)) {
            bankAccountInstructions.forEach((account) => {
                var code : string = `${account.BankCodes?.BankCode || ""}${account.BankCodes?.BranchCode || ""}${account.BankCodes?.ExtraCode || ""} | ${account.AccountNumber || ""} | ${account.AccountName || ""}`;
                var isExist : boolean = false;

                options.forEach((value, key, map) => {
                    //replace whitespace with empty
                    if (value.toLowerCase().replace(/\s/g, "") == code.toLowerCase().replace(/\s/g, "")) {
                        isExist = true;
                        return;
                    }
                })

                if ((account.Owner == BankAccountOwner.Individual || account.Owner == BankAccountOwner.Joint) && !isExist) {
                    options.set(
                        account.Id,
                        code
                    );
                }
            });
        }

    return options;
};

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = { hasChecked: [], assets: [] };
    }

    componentDidMount() {
        super.componentDidMount();
        if (this.props.isReview) {
            this.setState({ hasChecked: [1, 2] });
        }
        InstructionDocumentTypeRequestTriggerActionsCreator.triggerByInstructionId(
            this.props.data.HierarchyId as string,
            [DocumentCategory.Illustration]
        );
        this.getSellableAssets();
    }

    private getSellableAssets = () => {
        const params = getQueryParameters();
        const hierarchyId = params["hierarchyid"];
        SubAccountTypeHelper.getSubAccountHierarchyId(hierarchyId, SubAccountType.Npr, (subAccountHierarchyId) => {
            getSellableAssets(this.props.type, hierarchyId, subAccountHierarchyId, (data) => {
                const updateAssets = getUpdatedAssetsToSell([], 0, data?.assetsToSell);
                this.setState({ assets: updateAssets });
            });
        });
    };

    isValid() {
        if (this.state.hasChecked.length < 2) {
            this.props.showErrorMsgPopUp(["You must accept all items to continue"], true);
        }
        return super.isValid() && this.state.hasChecked.length == 2;
    }

    handleChange(declarationIds: number[], isSelected: boolean) {
        const checked = this.state.hasChecked;
        if (isSelected) {
            declarationIds.length == 2
                ? this.setState({ hasChecked: declarationIds })
                : this.setState({ hasChecked: checked.concat(declarationIds) });
        } else {
            this.setState({ hasChecked: checked.filter((ele) => ele != declarationIds) });
        }
    }

    renderBankDetailsSection(): JSX.Element {
        const { data } = this.props;
        return (
            <>
                <h2 className="mt-48">Bank details</h2>
                <div className="row">
                    <div className="col-12 col-sm-fluid">
                        <h4>Before</h4>
                        <Typography
                            type="auto-width"
                            config={bankAccountDetailsConfig(data?.Contribution?.DrawdownIncomeAmendmentModel?.ExistingDrawdownIncome?.AnnualIncome?.IncomePaymentAllocations, data)}
                        />
                    </div>
                    <div className="col-12 col-sm-fluid">
                        <h4>After</h4>
                        <Typography
                            type="auto-width"
                            config={bankAccountDetailsConfig(data?.Contribution?.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.Income?.IncomePaymentAllocations, data)}
                        />
                    </div>
                </div>
                <hr />
            </>
        )
    }

    public render(): JSX.Element {
        const { data, arrangementId } = this.props;
        const assetList = this.state.assets;
        const drawdownIncomeAmendmentModel = data.Contribution.DrawdownIncomeAmendmentModel;
        const disinvestment = data?.Contribution.DrawdownIncomeAmendmentModel?.DrawdownIncomeAmendmentInstruction?.IncomeDisinvestmentAllocations ?? [];
        assetList.map((row, index) => {
            disinvestment.map((v, idx) => {
                if (row.Id == v.ProductId) {
                    v.Id = row.Id;
                    v.Name = row.Assets;
                    v.Value = row.MarketValue;
                }
            });
        });
        drawdownIncomeAmendmentModel.DrawdownIncomeAmendmentInstruction.IncomeDisinvestmentAllocations = disinvestment;

        return (
            <div className="col-md-10 col-12">
                <h2>Review</h2>

                <div>
                    <IllustrationsAvailable onRef={(ref) => this.appendChild(ref)} onChange={() => {}} data={data} />
                    <FinancialAdvice data={data.Contribution} />
                    <IncomePaymentDetails
                        data={data?.Contribution.DrawdownIncomeAmendmentModel}
                        arrangementId={arrangementId}
                    />
                    {this.renderBankDetailsSection()}
                    <Declaration
                        data={this.state.hasChecked}
                        onRef={(ref) => this.appendChild(ref)}
                        onChange={(declarationIds: number[], isSelected: boolean) => {
                            this.handleChange(declarationIds, isSelected);
                        }}
                    />
                </div>
            </div>
        );
    }
}
